// Onboarding in the browser (Features 4): the account's record (shared/onboarding.ts) as a hook, and one way to
// change it. A change is merged here first, so the screen follows at once, then sent as one workspace.put with the
// whole record; the server merges it again with what other devices sent. Owner only: on a board shared with this
// person (a coworker's pass) nothing is read or written.

import { cleanOnboarding, isOn, mergeOnboarding, stamp, type Onboarding, type StepId } from "../../shared/onboarding";
import type { BoardKind, ID } from "../../shared/types";
import { commit } from "./actions";
import { get, useStore } from "./store";
import { readStored, readStoredJson, removeStored } from "../lib/stored";

const NONE: Onboarding = {};

/** The account's record, or an empty one before the board has loaded (and on a board shared with this person). */
export function onboarding(): Onboarding {
  const s = get();
  return (!s.guest && s.workspace?.onboarding) || NONE;
}

/** A piece of the record, kept current as any device changes it. */
export function useOnboarding<T>(pick: (o: Onboarding) => T): T {
  return useStore((s) => pick((!s.guest && s.workspace?.onboarding) || NONE));
}

/** Whether this page can keep anything on the account: signed in to its own board, loaded. */
const mine = () => {
  const s = get();
  return !s.guest && !!s.workspace;
};

/**
 * Change the record: the fields given are merged in (a mark's later time wins, a map key by key) and the whole
 * record goes to the board in one save. Nothing is sent when nothing changed, so calling it twice costs one write.
 */
export function writeOnboarding(change: Onboarding, label = "Onboarding"): void {
  if (!mine()) return;
  const was = onboarding();
  const next = mergeOnboarding(was, change);
  if (JSON.stringify(next) === JSON.stringify(cleanOnboarding(was))) return;
  commit([{ op: "workspace.put", patch: { onboarding: next } }], label, { history: false });
}

/** Tick a checklist step off, or untick it. */
export const tickStep = (id: StepId, on: boolean) => writeOnboarding({ ticked: { [id]: stamp(on) } }, on ? "Tick step" : "Untick step");
/** Put the checklist away for good, on every device. */
export const hideChecklist = () => writeOnboarding({ hidden: stamp(true) }, "Hide checklist");
/** A board kind's tips were seen or skipped (OB). */
export const sawTips = (kind: BoardKind) => writeOnboarding({ tips: { [kind]: Date.now() } }, "Tips seen");
/** Every tip and hint on or off (OB, Settings). */
export const setTipsOff = (off: boolean) => writeOnboarding({ tipsOff: stamp(off) }, off ? "Tips off" : "Tips on");
/** A hint shown once (OB). */
export const sawHint = (id: string) => writeOnboarding({ hints: { [id]: Date.now() } }, "Hint seen");
/** Where the user is now (OB): call it once a board or page has been open a moment, never per keystroke. */
export const keepPlace = (board: ID, page: ID | null = null, dev?: string) => writeOnboarding({ last: { board, page, at: Date.now(), ...(dev ? { dev } : {}) } }, "Place");
/** The What's new note seen (OB). */
export const sawNews = (id: string) => writeOnboarding({ news: { id, at: Date.now() } }, "What's new seen");

export { isOn };

// ---------------------------------------------------------------------------
// the flags this browser kept before the account did (Corner.tsx, until 26 Oct)

const OLD = { ticked: "cb:connect:ticked", done: "cb:onboard:done", seen: "cb:onboard:seen", themed: "cb:themed" };

/** Whether this browser still holds the old "hide for good" flag (scripts set it to keep the corner clear). */
export function oldHidden(): boolean {
  return readStored(OLD.done) === "1";
}

/**
 * Read this browser's old flags into the account, once, and drop them here. A step someone ticked before keeps its
 * tick, and the checklist they hid stays hidden. The old flags never said when, so they count as set long ago: a
 * change made since on any device wins over them.
 */
export function adoptOldFlags(): void {
  if (!mine()) return;
  const v = readStoredJson<unknown>(OLD.ticked, []);
  const ticked = Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  const done = readStored(OLD.done) === "1";
  const seen = readStored(OLD.seen) === "1";
  const themed = readStored(OLD.themed) === "1";
  if (!ticked.length && !done && !seen && !themed) return;
  // long ago: the first millisecond, so anything set since wins
  const LONG_AGO = 1;
  writeOnboarding(
    {
      ...(ticked.length ? { ticked: Object.fromEntries(ticked.map((id) => [id, LONG_AGO])) } : {}),
      ...(done ? { hidden: LONG_AGO } : {}),
      ...(seen ? { seen: LONG_AGO } : {}),
      ...(themed ? { themed: LONG_AGO } : {}),
    },
    "Keep onboarding",
  );
  for (const k of Object.values(OLD)) removeStored(k); // out of reach: read again next time, which changes nothing
}

/** A board's look was changed for the first time (the theme studio, a theme picked). */
export function markThemed(): void {
  if (onboarding().themed) return;
  writeOnboarding({ themed: Date.now() }, "Themed");
}
